import React from 'react';
import { Paper, Skeleton, Stack, Typography } from '@mui/material';
import { formatPeriodRange } from '../../lead-to-cash/components/PeriodSelector';
import type { DialOpPeriodRange } from '../../../types/dialingOpportunity';

function PeriodLabel({ label, range }: { label: string; range: DialOpPeriodRange }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap" useFlexGap>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          minWidth: 88,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.45 }}>
        {formatPeriodRange(range.start, range.end, 'week')}
      </Typography>
    </Stack>
  );
}

interface DialOpPeriodContextProps {
  loading?: boolean;
  currentPeriod?: DialOpPeriodRange | null;
  priorPeriod?: DialOpPeriodRange | null;
  compareEnabled?: boolean;
  methodologyNote?: string | null;
}

export default function DialOpPeriodContext({
  loading = false,
  currentPeriod,
  priorPeriod,
  compareEnabled = true,
  methodologyNote,
}: DialOpPeriodContextProps) {
  if (loading) {
    return (
      <Paper
        variant="outlined"
        sx={(t) => ({
          mb: 2,
          px: 2,
          py: 1.5,
          borderRadius: `${t.ds?.radius?.md ?? 8}px`,
        })}
      >
        <Skeleton variant="rounded" width="min(100%, 480px)" height={20} animation="wave" />
        <Skeleton variant="rounded" width="min(100%, 360px)" height={16} sx={{ mt: 1 }} animation="wave" />
      </Paper>
    );
  }

  if (!currentPeriod?.start || !currentPeriod?.end) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.8125rem' }}>
        Loading period…
      </Typography>
    );
  }

  const showCompare =
    compareEnabled && priorPeriod?.start && priorPeriod?.end;

  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        mb: 2,
        px: 2,
        py: 1.5,
        borderRadius: `${t.ds?.radius?.md ?? 8}px`,
        bgcolor: t.tokens?.surface?.raised ?? 'background.paper',
      })}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2.5 }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexWrap="wrap"
        useFlexGap
      >
        <PeriodLabel label="Current" range={currentPeriod} />
        {showCompare ? (
          <>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontWeight: 700, px: { sm: 0.5 }, display: { xs: 'none', sm: 'block' } }}
            >
              vs
            </Typography>
            <PeriodLabel label="Compare to" range={priorPeriod} />
          </>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Comparison off — metric deltas hidden
          </Typography>
        )}
      </Stack>
      {methodologyNote ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1.25, lineHeight: 1.45 }}
        >
          {methodologyNote}
        </Typography>
      ) : null}
    </Paper>
  );
}
